'use client';
import { jsx, jsxs } from 'react/jsx-runtime';
import { useState, useEffect, useContext, useRef, createContext } from 'react';
import { twMerge } from 'tailwind-merge';
import Typography from '../../Base/Typography/Typography.js';
import Box from '../../Base/Layouts/Box/Box.js';
import '../../Base/Layouts/FullBleed/FullBleed.js';

function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const MultiColumnListContext = /*#__PURE__*/ createContext({
    activeCategory: 0,
    currentActiveCategory: 0,
    handleCategoryClick: ()=>{}
});
/**
 * MultiColumnList의 최상위 컴포넌트입니다.
 *
 * @param {RootProps} props
 * @param {React.ReactNode} props.children - 자식 컴포넌트
 * @param {string} [props.className] - 추가 스타일 클래스
 * @param {number} [props.currentActiveCategory=0] - 현재 활성화된 카테고리 인덱스
 * @param {(index: number) => void} [props.onCategoryChange] - 카테고리 변경 시 호출되는 콜백
 *
 * @example
 * ```tsx
 * <MultiColumnList.Root onCategoryChange={(index) => console.log(index)}>
 *   <MultiColumnList.CategoryContainer>
 *     <MultiColumnList.Category index={0}>카테고리 1</MultiColumnList.Category>
 *     <MultiColumnList.Category index={1}>카테고리 2</MultiColumnList.Category>
 *   </MultiColumnList.CategoryContainer>
 *   <MultiColumnList.ListContainer>
 *     <MultiColumnList.List index={0}>내용 1</MultiColumnList.List>
 *     <MultiColumnList.List index={1}>내용 2</MultiColumnList.List>
 *   </MultiColumnList.ListContainer>
 * </MultiColumnList.Root>
 * ```
 */ const Root = (_param)=>{
    var { children, className, currentActiveCategory = 0, onCategoryChange } = _param, rest = _object_without_properties(_param, [
        "children",
        "className",
        "currentActiveCategory",
        "onCategoryChange"
    ]);
    const [activeCategory, setActiveCategory] = useState(currentActiveCategory);
    const handleCategoryClick = (index)=>{
        if (index === activeCategory) return; // 현재 카테고리를 다시 클릭하는 경우 방지
        onCategoryChange === null || onCategoryChange === void 0 ? void 0 : onCategoryChange(index);
        setActiveCategory(index);
    };
    useEffect(()=>{
        if (currentActiveCategory !== activeCategory) {
            setActiveCategory(currentActiveCategory);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        currentActiveCategory
    ]);
    return /*#__PURE__*/ jsx(MultiColumnListContext.Provider, {
        value: {
            activeCategory,
            currentActiveCategory,
            handleCategoryClick
        },
        children: /*#__PURE__*/ jsx(Box, _object_spread_props(_object_spread({
            "data-testid": "design-system-multi-column-list--root",
            className: twMerge('flex h-full w-full rounded-xl border border-w-gray-200 bg-w-white', className)
        }, rest), {
            children: children
        }))
    });
};
/**
 * 카테고리 목록을 포함하는 컨테이너 컴포넌트입니다.
 *
 * @param {RootProps} props
 * @param {React.ReactNode} props.children - Category 컴포넌트들
 * @param {string} [props.className] - 추가 스타일 클래스
 *
 * @example
 * ```tsx
 * <MultiColumnList.CategoryContainer>
 *   <MultiColumnList.Category index={0}>카테고리 1</MultiColumnList.Category>
 *   <MultiColumnList.Category index={1}>카테고리 2</MultiColumnList.Category>
 * </MultiColumnList.CategoryContainer>
 * ```
 */ const CategoryContainer = (_param)=>{
    var { className, children } = _param, rest = _object_without_properties(_param, [
        "className",
        "children"
    ]);
    return /*#__PURE__*/ jsx(Box, _object_spread_props(_object_spread({
        "data-testid": "design-system-multi-column-list--category-container",
        className: twMerge('flex h-full w-[180px] shrink-0 flex-col overflow-y-auto border-r border-w-gray-200 py-2 pl-2 scrollbar-list', className)
    }, rest), {
        children: children
    }));
};
/**
 * 선택된 카테고리의 내용을 표시하는 컨테이너 컴포넌트입니다.
 *
 * @param {RootProps} props
 * @param {React.ReactNode} props.children - List 컴포넌트들
 * @param {string} [props.className] - 추가 스타일 클래스
 *
 * @example
 * ```tsx
 * <MultiColumnList.ListContainer>
 *   <MultiColumnList.List index={0}>내용 1</MultiColumnList.List>
 *   <MultiColumnList.List index={1}>내용 2</MultiColumnList.List>
 * </MultiColumnList.ListContainer>
 * ```
 */ const ListContainer = (_param)=>{
    var { children, className } = _param, rest = _object_without_properties(_param, [
        "children",
        "className"
    ]);
    const { activeCategory } = useContext(MultiColumnListContext);
    const ref = useRef(null);
    useEffect(()=>{
        if (ref.current) {
            ref.current.scrollTo({
                top: 0
            });
        }
    }, [
        activeCategory
    ]);
    return /*#__PURE__*/ jsx(Box, _object_spread_props(_object_spread({
        "data-testid": "design-system-multi-column-list--list-container",
        ref: ref,
        className: twMerge('flex h-full w-full flex-col gap-6 overflow-y-auto px-6 py-5 scrollbar-list', className)
    }, rest), {
        children: children
    }));
};
/**
 * 개별 카테고리 항목 컴포넌트입니다.
 *
 * @param {CategoryProps} props
 * @param {React.ReactNode} props.children - 카테고리 텍스트
 * @param {string} [props.className] - 추가 스타일 클래스
 * @param {number} props.index - 카테고리 인덱스
 *
 * @example
 * ```tsx
 * <MultiColumnList.Category index={0}>카테고리 1</MultiColumnList.Category>
 * ```
 */ const Category = (_param)=>{
    var { children, className, index } = _param, rest = _object_without_properties(_param, [
        "children",
        "className",
        "index"
    ]);
    const { handleCategoryClick, activeCategory } = useContext(MultiColumnListContext);
    const isSelected = index === activeCategory;
    return /*#__PURE__*/ jsx(Box, _object_spread_props(_object_spread({
        "data-testid": "design-system-multi-column-list--category",
        className: twMerge('w-full cursor-pointer rounded-xl px-4 py-3 text-w-gray-600 hover:bg-primary-10', isSelected && 'font-medium text-primary', className),
        onClick: ()=>index !== undefined && handleCategoryClick(index)
    }, rest), {
        children: /*#__PURE__*/ jsx(Typography, {
            variant: "body1",
            className: "whitespace-normal break-all",
            children: children
        })
    }));
};
/**
 * 카테고리에 해당하는 내용을 표시하는 컴포넌트입니다.
 *
 * @param {ListProps} props
 * @param {React.ReactNode} props.children - 리스트 내용
 * @param {string} [props.title] - 리스트 제목
 * @param {number} props.index - 리스트 인덱스 (해당 카테고리 인덱스와 일치해야 표시됨)
 *
 * @example
 * ```tsx
 * <MultiColumnList.List index={0} title="섹션 제목">
 *   <div>리스트 내용</div>
 * </MultiColumnList.List>
 * ```
 */ const List = (_param)=>{
    var { children, title, index } = _param, rest = _object_without_properties(_param, [
        "children",
        "title",
        "index"
    ]);
    const { activeCategory } = useContext(MultiColumnListContext);
    if (index !== activeCategory) return null;
    return /*#__PURE__*/ jsxs(Box, _object_spread_props(_object_spread({
        "data-testid": "design-system-multi-column-list--list-item",
        className: "flex w-full flex-col gap-4"
    }, rest), {
        children: [
            title && /*#__PURE__*/ jsx(Typography, {
                variant: "body1",
                className: "w-full font-medium text-primary",
                "data-testid": "design-system-multi-column-list-title",
                children: title
            }),
            children
        ]
    }));
};

export { Category, CategoryContainer, List, ListContainer, Root };
